import { http } from '../helpers/easyindustriaapi/config';
import Swal from 'sweetalert2';
import Vue from 'vue';
import PatientReportForm from '../components/PatientReportForm.vue';
import { userHasPermission } from '@/helpers/userPermissions';

export default class ReportController {
    // Os atalhos fiscais não usam o PDF/JSON legado como preparação de emissão.
    openNfseBilling(router) {
        if (!this.currentEmpresa?.id || !userHasPermission(this.currentUser, 'faturamento.evolucoes.view')) {
            return Promise.resolve({ success: false, message: 'Sem permissão para consultar evoluções fiscais.' });
        }
        return router.push({ name: 'faturamento-evolucoes' });
    }
    constructor() {
        this.currentUser = JSON.parse(localStorage.getItem('user'));
        this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa'));
        this.listAgreements = []; // Lista de convênios - deve ser carregada via API
        this.listProfessionals = []; // Lista de profissionais - deve ser carregada via API
        this.listSpecialties = []; // Lista de especialidades - deve ser carregada via API
    }

    // Método para carregar a lista de convênios
    async loadAgreements() {
        try {
            const response = await http.get(`/agreement/list?empresa_id=${this.currentEmpresa.id}`);
            this.listAgreements = response.data.agreements || response.data || [];
            return this.listAgreements;
        } catch (error) {
            this.listAgreements = [];
            console.log(error);
            // Se contem "status code 403", mostra Toast com "Erro de permissão"
            if (error == 'Request failed with status code 403') {
              this.makeToast("danger", "O usuário logado não tem permissão para gerar o relatório");
            } else {
              this.makeToast("danger", error);
            }
            return [];
        }
    }

    // Método para carregar a lista de especialidades
    async loadSpecialties() {
        try {
            const response = await http.get(`/specialty/pluck?empresa_id=${this.currentEmpresa.id}`);
            this.listSpecialties = response.data || [];
            return this.listSpecialties;
        } catch (error) {
            console.log('Erro ao carregar especialidades:', error);
            this.listSpecialties = [];
            return [];
        }
    }

    // Método para exibir toast (assumindo que será implementado globalmente)
    makeToast(variant, message) {
        // Implementação básica - pode ser substituída por uma implementação global
        if (variant === 'success') {
            Swal.fire({
                icon: 'success',
                title: 'Sucesso!',
                text: message,
                timer: 3000,
                showConfirmButton: false
            });
        } else if (variant === 'danger' || variant === 'error') {
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: message
            });
        }
    }

    async showPatientReport() {
        try {
            // Carregar convênios e especialidades se ainda não foram carregados
            if (this.listAgreements.length === 0) {
                await this.loadAgreements();
            }
            if (this.listSpecialties.length === 0) {
                await this.loadSpecialties();
            }

            // Carregar valores salvos do localStorage
            const savedDateIni = localStorage.getItem('patientReport_date_ini') || '';
            const savedDateEnd = localStorage.getItem('patientReport_date_end') || '';
            const savedAgreementId = localStorage.getItem('patientReport_agreement_id') || '';
            const savedSpecialtyIds = localStorage.getItem('patientReport_specialty_ids') || '[]';
            
            // Usar modal personalizado com componentes Vue
            return this.showPatientReportModal(savedDateIni, savedDateEnd, savedAgreementId, savedSpecialtyIds);
        } catch (error) {
            console.log('Erro no showPatientReport:', error);
            const message = error.response?.data?.message || 'Erro ao processar relatório';
            this.makeToast("danger", message);
        }
    }

    async showPatientReportModal(savedDateIni, savedDateEnd, savedAgreementId, savedSpecialtyIds) {
        try {
            // Preparar dados iniciais para o componente
            const selectedAgreement = this.listAgreements.find(a => a.id == savedAgreementId);
            const selectedSpecialties = this.listSpecialties.filter(s => 
                JSON.parse(savedSpecialtyIds).includes(s.id)
            );
            
            const initialData = {
                dateIni: savedDateIni,
                dateEnd: savedDateEnd,
                selectedAgreement: selectedAgreement,
                selectedSpecialties: selectedSpecialties
            };

            // Criar um div para montar o componente
            const wrapper = document.createElement("div");
            
            // Criar instância do componente PatientReportForm
            const componentPatientReportForm = new (Vue.extend(PatientReportForm))({
                propsData: {
                    listAgreements: this.listAgreements,
                    listSpecialties: this.listSpecialties,
                    initialData: initialData
                }
            }).$mount();
            
            // Adicionar o componente ao div
            wrapper.appendChild(componentPatientReportForm.$el);

            const result = await Swal.fire({
                title: "Relatório de Atendimentos",
                footer: "<span class='text-danger'>Relatório de Atendimentos por Convênio, para emissão de NFS-e</span>",
                allowOutsideClick: false,
                showLoaderOnConfirm: true,
                confirmButtonText: "Visualizar",
                showCancelButton: true,
                cancelButtonText: "Fechar",
                html: wrapper,
                focusConfirm: false,
                preConfirm: () => {
                    const self = this; // Mantém referência ao controller
                    
                    // Validar os dados do componente
                    const validation = componentPatientReportForm.validate();
                    if (!validation.valid) {
                        Swal.showValidationMessage(validation.message);
                        return false;
                    }
                    
                    // Obter dados do formulário
                    const formData = componentPatientReportForm.getFormData();
                    
                    // Salvar valores no localStorage
                    localStorage.setItem('patientReport_date_ini', formData.dateIni);
                    localStorage.setItem('patientReport_date_end', formData.dateEnd);
                    localStorage.setItem('patientReport_agreement_id', formData.agreementId);
                    localStorage.setItem('patientReport_specialty_ids', JSON.stringify(formData.specialtyIds));
                    
                    const params = {
                        start_date: formData.dateIni,
                        end_date: formData.dateEnd,
                        agreement_id: formData.agreementId,
                        specialty_ids: formData.specialtyIds.length > 0 ? formData.specialtyIds : null,
                        empresa_id: self.currentEmpresa.id
                    };
                    
                    return http.get('/atendimento/relatorio/paciente-relatorio', {
                        params: params,
                        responseType: 'blob'
                    }).then(response => {
                        const blob = new Blob([response.data], { type: "application/pdf" });
                        const url = window.URL.createObjectURL(blob);
                        window.open(url, "_blank"); // Abre o PDF em uma nova aba
                        return true;
                    }).catch(error => {
                        console.log('Erro ao gerar relatório:', error);
                        const message = error.response?.data?.message || 'Erro ao gerar relatório';
                        self.makeToast("danger", message);
                        throw error;
                    });
                }
            });
            
            // Destruir o componente após o modal fechar
            componentPatientReportForm.$destroy();
            
            if (result.isConfirmed) {
                this.makeToast("success", "Relatório gerado com sucesso!");
            }
        } catch (error) {
            console.log('Erro no showPatientReportModal:', error);
            const message = error.response?.data?.message || 'Erro ao processar relatório';
            this.makeToast("danger", message);
        }
    }

    // Método para carregar a lista de profissionais
    async loadProfessionals() {
        try {
            const response = await http.get(`/professional?empresa_id=${this.currentEmpresa.id}`);
            this.listProfessionals = response.data || [];
            return this.listProfessionals;
        } catch (error) {
            console.log('Erro ao carregar profissionais:', error);
            this.listProfessionals = [];
            return [];
        }
    }

    async showAmountTransfer() {
        try {
            // Carregar profissionais se ainda não foram carregados
            if (!this.listProfessionals || this.listProfessionals.length === 0) {
                await this.loadProfessionals();
            }

            // Carregar valores salvos do localStorage
            const savedDateIni = localStorage.getItem('amountTransfer_date_ini') || '';
            const savedDateEnd = localStorage.getItem('amountTransfer_date_end') || '';
            const savedProfessionalId = localStorage.getItem('amountTransfer_professional_id') || '';
            
            const result = await Swal.fire({
                title: 'Relatório de Repasses',
                footer: 'Preencha os campos para filtrar relatório',
                allowOutsideClick: false,
                showLoaderOnConfirm: true,
                confirmButtonText: "Visualizar",
                showCancelButton: true,
                cancelButtonText: "Fechar",
                html: `
                    <div class="row">
                        <div class="col-md-6">
                            <label for="date_ini">Data Inicial</label>
                            <input value="${savedDateIni}" type="date" id="date_ini" class="form-control" required>
                        </div>
                        <div class="col-md-6">
                            <label for="date_end">Data Final</label>
                            <input value="${savedDateEnd}" type="date" id="date_end" class="form-control" required>
                        </div>
                        <div class="col-md-12 mt-3">
                            <label for="professional">Profissional</label>
                            <select id="professional" class="form-control" required>
                                <option value="">Selecione um Profissional</option>
                                ${this.listProfessionals.map(function(professional) {
                                    const selected = professional.id == savedProfessionalId ? 'selected' : '';
                                    return `<option value="${professional.id}" ${selected}>${professional.name}</option>`;
                                }).join('')}
                            </select>
                        </div>
                    </div>`,
                focusConfirm: false,
                preConfirm: () => {
                    const self = this; // Mantém referência ao controller
                    return new Promise((resolve, reject) => {
                        const dateIni = document.getElementById("date_ini").value;
                        const dateEnd = document.getElementById("date_end").value;
                        const professionalId = document.getElementById("professional").value;
                        
                        // Validações
                        if (!dateIni) {
                            Swal.showValidationMessage('Data inicial é obrigatória');
                            return;
                        }
                        if (!dateEnd) {
                            Swal.showValidationMessage('Data final é obrigatória');
                            return;
                        }
                        if (!professionalId) {
                            Swal.showValidationMessage('Por favor, selecione um profissional');
                            return;
                        }
                        if (new Date(dateIni) > new Date(dateEnd)) {
                            Swal.showValidationMessage('Data inicial não pode ser maior que a data final');
                            return;
                        }

                        // Salvar valores no localStorage
                        localStorage.setItem('amountTransfer_date_ini', dateIni);
                        localStorage.setItem('amountTransfer_date_end', dateEnd);
                        localStorage.setItem('amountTransfer_professional_id', professionalId);

                        const params = {
                            start_date: dateIni,
                            end_date: dateEnd,
                            professional_id: professionalId,
                            empresa_id: self.currentEmpresa.id
                        };

                        http.get('/atendimento/relatorio/repasse-profissional', {
                            params: params,
                            responseType: 'blob'
                        }).then(response => {
                            const blob = new Blob([response.data], { type: "application/pdf" });
                            const url = window.URL.createObjectURL(blob);
                            window.open(url, "_blank"); // Abre o PDF em uma nova aba
                            resolve();
                        }).catch(error => {
                            console.log('Erro ao gerar relatório de repasse:', error);
                            const message = error.response?.data?.message || 'Erro ao gerar relatório';
                            self.makeToast('danger', message);
                            reject(error);
                        });
                    });
                }
            });

            if (result.isConfirmed) {
                this.makeToast("success", "Relatório gerado com sucesso!");
            }
        } catch (error) {
            console.log('Erro no showAmountTransfer:', error);
            const message = error.response?.data?.message || 'Erro ao processar relatório';
            this.makeToast("danger", message);
        }
    }

    async showCalendarReport(profissionals) {
        try {
            // Debug: ver o que está sendo passado
            console.log('profissionals:', profissionals);
            console.log('tipo:', typeof profissionals);
            console.log('é array:', Array.isArray(profissionals));
            
            // Validar se o profissional está definido
            if (!profissionals) {
            this.makeToast("danger", "Por favor, selecione um profissional no filtro para gerar o relatório de agenda");
            return;
        }

        let professionalId = -1;
        let professionalName = '';

        // Se for array
        if (Array.isArray(profissionals)) {
            if (profissionals.length === 0) {
                this.makeToast("danger", "Por favor, selecione um profissional no filtro para gerar o relatório de agenda");
                return;
            }
            
            if (profissionals.length > 1) {
                this.makeToast("danger", "Por favor, selecione apenas um profissional no filtro para gerar o relatório de agenda");
                return;
            }

            professionalId = profissionals[0].id;
            professionalName = profissionals[0].name || profissionals[0].nome;
        } 
        // Se for objeto
        else if (typeof profissionals === 'object') {
            if (!profissionals.id) {
                this.makeToast("danger", "Por favor, selecione um profissional válido no filtro para gerar o relatório de agenda");
                return;
            }

            professionalId = profissionals.id;
            professionalName = profissionals.name || profissionals.nome;
        }
        // Se não for nem array nem objeto
        else {
            this.makeToast("danger", "Formato de profissional inválido. Por favor, selecione um profissional no filtro");
            return;
        }

            // Confirmar geração do relatório
            const result = await Swal.fire({
                title: 'Relatório de Agenda Livre',
                text: `Gerar relatório de agenda para ${professionalName}?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Gerar Relatório',
                cancelButtonText: 'Cancelar',
                showLoaderOnConfirm: true,
                preConfirm: () => {
                    const self = this;
                    
                    // Primeiro, tentar chamar o backend
                    return http.get('/agenda/reportCalendarProfessional', {
                        params: {
                            professional_id: professionalId
                        },
                        responseType: 'blob'
                    }).then(response => {
                        console.log('Response:', response);
                        console.log('Response data:', response.data);
                        console.log('Response headers:', response.headers);
                        
                        // Verificar se a resposta é realmente um PDF
                        const contentType = response.headers['content-type'];
                        console.log('Content-Type:', contentType);
                        
                        if (contentType && contentType.includes('application/pdf')) {
                            const blob = new Blob([response.data], { type: "application/pdf" });
                            const url = window.URL.createObjectURL(blob);
                            
                            // Verificar se o blob foi criado corretamente
                            console.log('Blob size:', blob.size);
                            console.log('Blob URL:', url);
                            
                            if (blob.size > 0) {
                                // Tentar abrir em nova aba primeiro
                                const newWindow = window.open(url, "_blank");
                                
                                // Se não conseguir abrir (bloqueador de popup), tentar download
                                if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
                                    console.log('Popup bloqueado, tentando download...');
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = `agenda-${professionalName.replace(/\s+/g, '-')}.pdf`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    self.makeToast("success", "PDF baixado com sucesso!");
                                }
                                
                                // Limpar URL após um tempo para liberar memória
                                setTimeout(() => {
                                    window.URL.revokeObjectURL(url);
                                }, 1000);
                                
                                return true;
                            } else {
                                self.makeToast("danger", "PDF gerado está vazio");
                                return false;
                            }
                        } else if (contentType && contentType.includes('text/html')) {
                            // Se for HTML (modo debug), abrir em nova aba
                            console.log('Resposta HTML recebida (modo debug)');
                            const htmlBlob = new Blob([response.data], { type: "text/html" });
                            const htmlUrl = window.URL.createObjectURL(htmlBlob);
                            window.open(htmlUrl, "_blank");
                            self.makeToast("success", "Relatório exibido em nova aba (modo HTML)");
                            return true;
                        } else {
                            // Se não for PDF, pode ser HTML (debug) ou JSON (erro)
                            const reader = new FileReader();
                            reader.onload = function() {
                                try {
                                    const errorResponse = JSON.parse(reader.result);
                                    console.log('Error response:', errorResponse);
                                    self.makeToast("danger", errorResponse.message || "Erro ao gerar relatório");
                                } catch (e) {
                                    console.log('Response as text:', reader.result);
                                    
                                    // Se for HTML, pode ser o fallback de debug
                                    if (reader.result.includes('<html') || reader.result.includes('<!DOCTYPE')) {
                                        console.log('Resposta HTML recebida (modo debug)');
                                        // Abrir HTML em nova aba para debug
                                        const htmlBlob = new Blob([reader.result], { type: "text/html" });
                                        const htmlUrl = window.URL.createObjectURL(htmlBlob);
                                        window.open(htmlUrl, "_blank");
                                        self.makeToast("warning", "Modo debug: HTML exibido em nova aba");
                                    } else {
                                        self.makeToast("danger", "Resposta inválida do servidor");
                                    }
                                }
                            };
                            reader.readAsText(response.data);
                            return false;
                        }
                    }).catch(error => {
                        console.log('Erro ao chamar backend:', error);
                        
                        // Mostrar erro ao usuário
                        const message = error.response?.data?.message || 'Erro ao gerar relatório de agenda no servidor';
                        self.makeToast("danger", message);
                        return false;
                    });
                }
            });

            if (result.isConfirmed) {
                this.makeToast("success", "Relatório de agenda gerado com sucesso!");
            }
        } catch (error) {
            console.log('Erro no showCalendarReport:', error);
            const message = error.response?.data?.message || 'Erro ao processar relatório de agenda';
            this.makeToast("danger", message);
        }
    }

    generateMockReport(professionalName) {
        console.log('Gerando relatório HTML temporário para:', professionalName);
        
        // Dados mockados para demonstração
        const mockData = {
            professional: {
                nome: professionalName || 'Profissional Exemplo'
            },
            agendaByDay: {
                'Segunda-feira': [
                    {
                        start_time: '08:00:00',
                        end_time: '09:00:00',
                        pacient: { nome: 'João Silva' },
                        is_free: false
                    },
                    {
                        start_time: '09:00:00',
                        end_time: '10:00:00',
                        pacient: { nome: 'livre' },
                        is_free: true
                    },
                    {
                        start_time: '10:00:00',
                        end_time: '11:00:00',
                        pacient: { nome: 'Maria Santos' },
                        is_free: false
                    },
                    {
                        start_time: '14:00:00',
                        end_time: '15:00:00',
                        pacient: { nome: 'livre' },
                        is_free: true
                    },
                    {
                        start_time: '15:00:00',
                        end_time: '16:00:00',
                        pacient: { nome: 'Pedro Costa' },
                        is_free: false
                    }
                ],
                'Terça-feira': [
                    {
                        start_time: '08:30:00',
                        end_time: '09:30:00',
                        pacient: { nome: 'Ana Oliveira' },
                        is_free: false
                    },
                    {
                        start_time: '10:00:00',
                        end_time: '11:00:00',
                        pacient: { nome: 'livre' },
                        is_free: true
                    },
                    {
                        start_time: '11:00:00',
                        end_time: '12:00:00',
                        pacient: { nome: 'Carlos Lima' },
                        is_free: false
                    }
                ],
                'Quarta-feira': [
                    {
                        start_time: '09:00:00',
                        end_time: '10:00:00',
                        pacient: { nome: 'livre' },
                        is_free: true
                    },
                    {
                        start_time: '10:00:00',
                        end_time: '11:00:00',
                        pacient: { nome: 'Fernanda Rocha' },
                        is_free: false
                    },
                    {
                        start_time: '15:30:00',
                        end_time: '16:30:00',
                        pacient: { nome: 'livre' },
                        is_free: true
                    }
                ]
            }
        };

        // Gerar HTML temporário (até o backend funcionar)
        const htmlContent = this.generateReportHTML(mockData);
        
        // Criar blob e abrir em nova aba
        const htmlBlob = new Blob([htmlContent], { type: "text/html; charset=utf-8" });
        const htmlUrl = window.URL.createObjectURL(htmlBlob);
        
        const newWindow = window.open(htmlUrl, "_blank");
        if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
            // Fallback para download
            const link = document.createElement('a');
            link.href = htmlUrl;
            link.download = `agenda-${professionalName.replace(/\s+/g, '-')}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.makeToast("success", "Relatório HTML baixado (PDF será implementado no backend)!");
        } else {
            this.makeToast("warning", "Relatório HTML exibido (PDF será implementado no backend)!");
        }
        
        // Limpar URL após um tempo
        setTimeout(() => {
            window.URL.revokeObjectURL(htmlUrl);
        }, 1000);
    }


    generateReportHTML(data) {
        const { professional, agendaByDay } = data;
        
        let html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Agenda de ${professional.nome}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 20px;
            background-color: #f5f5f5;
        }
        
        .agenda-container {
            max-width: 1200px;
            margin: 0 auto;
            background-color: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        
        .agenda-title {
            text-align: center;
            color: #2c3e50;
            margin-bottom: 30px;
            font-size: 28px;
            border-bottom: 3px solid #3498db;
            padding-bottom: 15px;
        }
        
        .agenda-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }
        
        .agenda-table th {
            background-color: #3498db;
            color: white;
            padding: 15px;
            text-align: center;
            font-weight: bold;
            font-size: 16px;
        }
        
        .agenda-table td {
            padding: 12px 15px;
            border: 1px solid #ddd;
            text-align: center;
        }
        
        .time-cell {
            background-color: #ecf0f1;
            font-weight: bold;
            color: #2c3e50;
        }
        
        .day-cell {
            background-color: #34495e;
            color: white;
            font-weight: bold;
            font-size: 18px;
            vertical-align: middle;
        }
        
        .patient-cell {
            background-color: #ffffff;
        }
        
        .patient-cell.livre {
            color: #e74c3c;
            font-style: italic;
            font-weight: bold;
        }
        
        .empty-row td {
            height: 20px;
            background-color: #f8f9fa;
        }
        
        .no-data {
            text-align: center;
            padding: 40px;
            color: #7f8c8d;
            font-style: italic;
        }
    </style>
</head>
<body>
    <div class="agenda-container">
        <h1 class="agenda-title">Agenda de ${professional.nome}</h1>
        <table class="agenda-table">
            <thead>
                <tr>
                    <th>Horários</th>
                    <th>Dias da Semana</th>
                    <th>Pacientes</th>
                </tr>
            </thead>
            <tbody>`;

        // Verificar se há dados
        if (Object.keys(agendaByDay).length === 0) {
            html += `
                <tr>
                    <td colspan="3" class="no-data">Nenhum agendamento encontrado para este profissional.</td>
                </tr>`;
        } else {
            // Gerar linhas para cada dia
            Object.keys(agendaByDay).forEach((day, dayIndex) => {
                const dayAgendas = agendaByDay[day];
                let isFirstRow = true;
                
                dayAgendas.forEach((agenda) => {
                    html += `
                    <tr>
                        <td class="time-cell">${this.formatTime(agenda.start_time)} às ${this.formatTime(agenda.end_time)}</td>`;
                    
                    if (isFirstRow) {
                        html += `<td class="day-cell" rowspan="${dayAgendas.length}">${day}</td>`;
                        isFirstRow = false;
                    }
                    
                    const patientClass = agenda.is_free ? 'livre' : '';
                    const patientName = agenda.pacient ? agenda.pacient.nome : 'Sem paciente';
                    
                    html += `<td class="patient-cell ${patientClass}">${patientName}</td>
                    </tr>`;
                });
                
                // Adicionar linha vazia entre os dias (exceto no último)
                if (dayIndex < Object.keys(agendaByDay).length - 1) {
                    html += `<tr class="empty-row"><td colspan="3">&nbsp;</td></tr>`;
                }
            });
        }

        html += `
            </tbody>
        </table>
    </div>
</body>
</html>`;

        return html;
    }

    formatTime(timeString) {
        if (!timeString) return '';
        const time = new Date(`2000-01-01T${timeString}`);
        return time.toLocaleTimeString('pt-BR', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        });
    }

    // Método para tratar erros (padrão dos outros controllers)
    handleError(error) {
        if (error.response) {
            return error.response.data;
        } else if (error.request) {
            return { message: 'Erro de conexão com o servidor' };
        } else {
            return { message: 'Erro desconhecido' };
        }
    }

}
